import React, { useEffect, useRef, useState, useCallback } from 'react';
import './Camera.css';
import IonicPageWrapper from '../../components/IonicPageWrapper/IonicPageWrapper';
import { useHistory } from 'react-router';
import { loadPlayer } from 'rtsp-relay/browser';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VideocamIcon from '@mui/icons-material/Videocam';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import { Chip, CircularProgress, IconButton } from '@mui/material';

import { formatDeviceId, getDeviceStreamUrl, getDevicePublishAudioUrl } from '../../config/streams';
import LiveStreamModal from '../../components/LiveStreamModal/LiveStreamModal';

const STREAMS = [
    { id: 'video', label: 'Video + Audio' },
    { id: 'thermal', label: 'Thermal' },
    { id: 'audio', label: 'Audio' },
];

const Camera = () => {
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();
    const isloggedin = localStorage.getItem("loggedIn");
    const user_id = localStorage.getItem("user_id");
    const auth = localStorage.getItem("authToken");

    const containerRef = useRef(null);
    const playerRef = useRef(null);
    const [activeTab, setActiveTab] = useState('video');
    const [streamPrefix, setStreamPrefix] = useState(() => localStorage.getItem('stream_prefix') || 'Cam');
    const [selectedCamId, setSelectedCamId] = useState('');
    const [userCameras, setUserCameras] = useState([]);
    const [loadingCameras, setLoadingCameras] = useState(true);
    const [subdataMap, setSubdataMap] = useState({});
    const [vlcModalOpen, setVlcModalOpen] = useState(false);
    const [isInlinePlaying, setIsInlinePlaying] = useState(true);
    const [isInlineMuted, setIsInlineMuted] = useState(false);
    const [isPublishingAudio, setIsPublishingAudio] = useState(false);
    const mediaRecorderRef = useRef(null);
    const pubWsRef = useRef(null);
    const micStreamRef = useRef(null);
    const prevMotionRef = useRef({});


    useEffect(() => {
        if (!isloggedin) {
            history.push('/login');
            window.location.reload();
        }
    }, [isloggedin, history]);

    // Fetch cameras belonging to the logged-in user
    const fetchUserCameras = useCallback(async () => {
        setLoadingCameras(true);
        try {
            const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:8080';
            const res = await axios.post(`${baseUrl}/admin/users/getusercameras`, { user_id }, {
                headers: { Authorization: `Bearer ${auth}` }
            });

            if (res.data && res.data.success && Array.isArray(res.data.cameras) && res.data.cameras.length > 0) {
                const list = res.data.cameras.map(dev => {
                    const camId = dev.camera_name || dev.device_name || `VC-${String(dev.device_id).padStart(3, '0')}`;
                    const camName = dev.camera_name || dev.device_name || `Camera ${dev.device_id}`;
                    return {
                        id: camId,
                        name: camName,
                        device_id: dev.device_id,
                        camera_id: dev.camera_id
                    };
                });
                setUserCameras(list);
                if (list.length > 0) {
                    setSelectedCamId(list[0].id);
                }
            } else {
                setUserCameras([]);
            }
        } catch (err) {
            console.error("Error fetching user cameras:", err);
            setUserCameras([]);
        } finally {
            setLoadingCameras(false);
        }
    }, [user_id, auth]);

    useEffect(() => {
        if (user_id) {
            fetchUserCameras();
        }
    }, [user_id, fetchUserCameras]);

    const selectedCamObj = userCameras.find(c => c.id === selectedCamId);
    const targetDeviceId = selectedCamObj ? (selectedCamObj.device_id || selectedCamObj.camera_id || selectedCamObj.id) : selectedCamId;
    const formattedDevId = formatDeviceId(targetDeviceId);
    const activeStreamUrl = getDeviceStreamUrl(activeTab, targetDeviceId, streamPrefix);
    const publishAudioUrl = getDevicePublishAudioUrl(targetDeviceId, streamPrefix);

    const currentStream = STREAMS.find(s => s.id === activeTab) || STREAMS[0];

    const stopPublishingAudio = () => {
        setIsPublishingAudio(false);
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            try { mediaRecorderRef.current.stop(); } catch (e) {}
        }
        if (micStreamRef.current) {
            try {
                micStreamRef.current.getTracks().forEach(track => track.stop());
            } catch (e) {}
            micStreamRef.current = null;
        }
        if (pubWsRef.current) {
            try { pubWsRef.current.close(); } catch (e) {}
            pubWsRef.current = null;
        }
    };

    const startPublishingAudio = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            micStreamRef.current = stream;

            const audioTrack = stream.getAudioTracks()[0];
            if (audioTrack) {
                audioTrack.onended = () => {
                    console.warn('Microphone track ended');
                    stopPublishingAudio();
                };
            }

            const wsBaseUrl = process.env.REACT_APP_BASE_URL
                ? process.env.REACT_APP_BASE_URL.replace(/^http/, 'ws')
                : 'wss://api.imphantomresearch.com:8080';

            const wsUrl = `${wsBaseUrl}/admin/stream/publish-audio?url=${encodeURIComponent(publishAudioUrl)}`;
            const ws = new WebSocket(wsUrl);
            pubWsRef.current = ws;

            ws.onopen = () => {
                console.log('Audio publishing WebSocket connected to:', publishAudioUrl);
                setIsPublishingAudio(true);

                try {
                    let recorderOptions = {};
                    if (typeof MediaRecorder.isTypeSupported === 'function') {
                        if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
                            recorderOptions = { mimeType: 'audio/webm;codecs=opus' };
                        } else if (MediaRecorder.isTypeSupported('audio/webm')) {
                            recorderOptions = { mimeType: 'audio/webm' };
                        } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
                            recorderOptions = { mimeType: 'audio/mp4' };
                        } else if (MediaRecorder.isTypeSupported('audio/aac')) {
                            recorderOptions = { mimeType: 'audio/aac' };
                        }
                    }

                    let recorder;
                    try {
                        recorder = Object.keys(recorderOptions).length > 0
                            ? new MediaRecorder(stream, recorderOptions)
                            : new MediaRecorder(stream);
                    } catch (e) {
                        console.warn('MediaRecorder with mimeType options failed, falling back to default:', e);
                        recorder = new MediaRecorder(stream);
                    }

                    mediaRecorderRef.current = recorder;

                    recorder.ondataavailable = async (e) => {
                        if (e.data && e.data.size > 0 && ws.readyState === WebSocket.OPEN) {
                            try {
                                const buffer = await e.data.arrayBuffer();
                                ws.send(buffer);
                            } catch (sendErr) {
                                console.error('Error sending audio data buffer:', sendErr);
                            }
                        }
                    };

                    recorder.onerror = (recErr) => {
                        console.error('MediaRecorder error:', recErr);
                        if (recorder.state === 'inactive') {
                            stopPublishingAudio();
                        }
                    };

                    recorder.start(500);
                } catch (recErr) {
                    console.error('MediaRecorder start error:', recErr);
                    alert('Microphone recording error: ' + (recErr.message || recErr));
                    stopPublishingAudio();
                }
            };

            ws.onerror = (err) => {
                console.error('Audio publishing WebSocket error:', err);
            };

            ws.onclose = () => {
                console.log('Audio publishing WebSocket closed');
                stopPublishingAudio();
            };
        } catch (err) {
            console.error('Failed to access microphone for audio publishing:', err);
            alert('Could not access microphone: ' + (err.name || err.message || err));
            stopPublishingAudio();
        }
    };


    const togglePublishAudio = () => {
        if (isPublishingAudio) {
            stopPublishingAudio();
        } else {
            startPublishingAudio();
        }
    };

    useEffect(() => {
        return () => {
            stopPublishingAudio();
        };
    }, []);


    useEffect(() => {
        if (activeTab === 'audio') return;

        let isSubscribed = true;
        let activePlayer = null;

        if (containerRef.current) {
            containerRef.current.innerHTML = '';
        }

        if (selectedCamId && containerRef.current) {
            const canvas = document.createElement('canvas');
            canvas.className = 'camera_canvas';
            canvas.style.width = '100%';
            canvas.style.height = '100%';
            canvas.style.display = 'block';
            containerRef.current.appendChild(canvas);

            const wsBaseUrl = process.env.REACT_APP_BASE_URL
                ? process.env.REACT_APP_BASE_URL.replace(/^http/, 'ws')
                : 'wss://api.imphantomresearch.com:8080';
            const streamWsUrl = `${wsBaseUrl}/admin/stream/api/stream?url=${encodeURIComponent(activeStreamUrl)}`;

            try {
                loadPlayer({
                    url: streamWsUrl,
                    canvas: canvas,
                    audio: activeTab !== 'thermal' && !isInlineMuted,
                    onDisconnect: () => {
                        if (isSubscribed) console.log('Stream connection lost!');
                    },
                }).then(p => {
                    if (isSubscribed) {
                        playerRef.current = p;
                        activePlayer = p;
                    }
                }).catch(err => {
                    console.error("Stream player load error:", err);
                });
            } catch (err) {
                console.error("Stream load error:", err);
            }
        }

        return () => {
            isSubscribed = false;
            if (activePlayer && typeof activePlayer.destroy === 'function') {
                try {
                    activePlayer.destroy();
                } catch (e) {}
            }
            playerRef.current = null;
            if (containerRef.current) {
                containerRef.current.innerHTML = '';
            }
        };
    }, [activeTab, activeStreamUrl, selectedCamId, isInlineMuted]);

    // Poll subdata for motion detection events
    const fetchSubdata = useCallback(async () => {
        try {
            const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:8080';
            const res = await axios.post(`${baseUrl}/admin/users/getsubdata`);
            if (res.data && res.data.success && Array.isArray(res.data.data)) {
                const map = {};
                res.data.data.forEach(item => {
                    const devId = item.cam_id || (item.data && item.data.device);
                    if (devId) {
                        map[devId] = item.data;
                    }
                });
                setSubdataMap(map);

                // Trigger snackbar notifications for motion detection status changes
                Object.keys(map).forEach(devId => {
                    const data = map[devId];
                    const isMotion = data.event === 'motion_detected' || data.status === 'active' || data.motion_detected === true;
                    const prevIsMotion = prevMotionRef.current[devId];

                    if (prevIsMotion === undefined) {
                        prevMotionRef.current[devId] = isMotion;
                    } else if (!prevIsMotion && isMotion) {
                        enqueueSnackbar(`🚨 Motion Detected on Camera ${devId}!`, {
                            variant: 'warning',
                            autoHideDuration: 4000
                        });
                        prevMotionRef.current[devId] = true;
                    } else if (prevIsMotion && !isMotion) {
                        enqueueSnackbar(`✅ Motion Cleared on Camera ${devId}`, {
                            variant: 'success',
                            autoHideDuration: 3000
                        });
                        prevMotionRef.current[devId] = false;
                    }
                });
            }
        } catch (err) {
            console.log("Error polling subdata:", err);
        }
    }, [enqueueSnackbar]);

    useEffect(() => {
        fetchSubdata();
        const interval = setInterval(fetchSubdata, 2500);
        return () => clearInterval(interval);
    }, [fetchSubdata]);

    const onBackClick = () => {
        history.push('/dashboard');
    };

    return (
        <IonicPageWrapper headerTitle='Camera & Motion Detection' onBackClick={onBackClick} showLogout={true}>
            <div id="lightsControl">
                {/* Tab Switcher for Video / Thermal / Audio & Prefix Selector */}
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '15px', flexWrap: 'wrap', marginBottom: '15px' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        {STREAMS.map(stream => (
                            <button
                                key={stream.id}
                                onClick={() => setActiveTab(stream.id)}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '20px',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontWeight: 'bold',
                                    backgroundColor: activeTab === stream.id ? '#1976d2' : '#e0e0e0',
                                    color: activeTab === stream.id ? '#fff' : '#333',
                                    transition: 'all 0.2s ease-in-out'
                                }}
                            >
                                {stream.label}
                            </button>
                        ))}
                    </div>

                    {/* Stream Prefix Selector (Cam vs Smartlight) */}
                    <div style={{ display: 'flex', alignItems: 'center', backgroundColor: '#ffffff', padding: '4px 12px', borderRadius: '20px', boxShadow: '0 2px 6px rgba(0,0,0,0.08)' }}>
                        <span style={{ fontSize: '12px', fontWeight: 'bold', marginRight: '8px', color: '#555' }}>Prefix:</span>
                        <select
                            value={streamPrefix}
                            onChange={(e) => {
                                const val = e.target.value;
                                setStreamPrefix(val);
                                localStorage.setItem('stream_prefix', val);
                            }}
                            style={{
                                border: '1px solid #1976d2',
                                borderRadius: '12px',
                                padding: '4px 8px',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                color: '#1976d2',
                                backgroundColor: '#f5f9ff',
                                outline: 'none',
                                cursor: 'pointer'
                            }}
                        >
                            <option value="Cam">Cam (Cam-{formattedDevId})</option>
                            <option value="Smartlight">Smartlight (Smartlight-{formattedDevId})</option>
                        </select>
                    </div>
                </div>

                {loadingCameras ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '30px' }}>
                        <CircularProgress />
                    </div>
                ) : userCameras.length > 0 ? (
                    <div className="camera_cardCont">
                        {userCameras.map((cam) => {
                            const devIdNum = String(cam.device_id || '').trim();
                            const camIdNum = String(cam.camera_id || '').trim();
                            const camIdClean = String(cam.id || '').trim();
                            const camNameClean = String(cam.name || '').trim();
                            const vcDev = devIdNum ? `VC-${devIdNum}` : '';
                            const vcCam = camIdNum ? `VC-${camIdNum}` : '';

                            const subData = subdataMap[devIdNum] ||
                                subdataMap[camIdNum] ||
                                subdataMap[camIdClean] ||
                                subdataMap[camNameClean] ||
                                subdataMap[vcDev] ||
                                subdataMap[vcCam] ||
                                Object.values(subdataMap).find(d => {
                                    if (!d) return false;
                                    const dev = String(d.device || d.Cam_ID || d.cam_id || '').trim();
                                    return dev === devIdNum ||
                                        dev === camIdNum ||
                                        dev === camIdClean ||
                                        dev === camNameClean ||
                                        dev === vcDev ||
                                        dev === vcCam ||
                                        (devIdNum && (dev.endsWith(`-${devIdNum}`) || dev === `VC-${devIdNum}`));
                                }) || {};

                            const isMotion = subData.event === 'motion_detected' || subData.status === 'active' || subData.motion_detected === true;

                            return (
                                <CameraCard
                                    key={cam.id}
                                    camId={cam.id}
                                    camName={cam.name}
                                    subData={subData}
                                    isMotion={isMotion}
                                    isSelected={selectedCamId === cam.id}
                                    onSelect={() => setSelectedCamId(cam.id)}
                                />
                            );
                        })}
                    </div>
                ) : (
                    <div style={{ textAlign: 'center', padding: '30px', background: '#ffffff', borderRadius: '12px', marginBottom: '15px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                        <VideocamIcon style={{ fontSize: '48px', color: '#999', marginBottom: '10px' }} />
                        <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#333' }}>No Camera Associated with this User</h3>
                        <p style={{ margin: 0, color: '#666', fontSize: '13px' }}>There are currently no camera devices assigned under your user account.</p>
                    </div>
                )}

                {selectedCamId && (
                    <div className="vlc_small_player_card">
                        {/* Header Bar */}
                        <div className="vlc_small_header">
                            <div className="vlc_small_title">
                                <span className="vlc_cone">▲</span>
                                <span>{currentStream.label} - {selectedCamId}</span>
                            </div>
                            <div className="vlc_small_actions">
                                <Chip label={`${streamPrefix}-${formattedDevId}`} color="primary" size="small" style={{ fontSize: '11px', height: '22px' }} />
                                <button className="vlc_popout_btn" onClick={() => setVlcModalOpen(true)}>
                                    <OpenInFullIcon style={{ fontSize: '13px', marginRight: '4px' }} /> Popout Player
                                </button>
                            </div>
                        </div>

                        {/* Video / Audio Compact Stage */}
                        <div className="vlc_small_screen">
                            <div ref={containerRef} className="vlc_small_canvas_wrapper" style={{ display: activeTab === 'audio' ? 'none' : 'flex' }}></div>

                            {activeTab === 'audio' && (
                                <div className="vlc_small_audio_view">
                                    <VolumeUpIcon style={{ fontSize: '42px', color: '#ff9800' }} />
                                    <p style={{ margin: '6px 0 0 0', fontSize: '12px', color: '#ccc' }}>Live Audio Stream Active</p>
                                    <audio
                                        controls
                                        autoPlay
                                        style={{ marginTop: '10px', width: '100%', maxWidth: '280px' }}
                                        src={`${process.env.REACT_APP_BASE_URL || 'http://localhost:8080'}/admin/stream/audio?url=${encodeURIComponent(activeStreamUrl)}`}
                                    >
                                        Your browser does not support HTML5 MP3 audio streaming.
                                    </audio>
                                </div>
                            )}

                            <div className="vlc_small_live_tag">
                                <span className="vlc_red_pulse">●</span> LIVE
                            </div>

                            {/* Active Mic Broadcasting Badge */}
                            {isPublishingAudio && (
                                <div style={{ position: 'absolute', top: '10px', left: '10px', backgroundColor: 'rgba(239, 83, 80, 0.95)', color: '#fff', padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '5px', zIndex: 10 }}>
                                    <MicIcon style={{ fontSize: '15px' }} /> <span>Mic Active</span>
                                </div>
                            )}
                        </div>

                        {/* VLC Controls Bar */}
                        <div className="vlc_small_controls">
                            <IconButton size="small" onClick={() => {
                                if (playerRef.current) {
                                    if (isInlinePlaying) {
                                        if (typeof playerRef.current.pause === 'function') playerRef.current.pause();
                                    } else {
                                        if (typeof playerRef.current.play === 'function') playerRef.current.play();
                                    }
                                }
                                setIsInlinePlaying(!isInlinePlaying);
                            }}>
                                {isInlinePlaying ? <PauseIcon style={{ color: '#ff9800', fontSize: '20px' }} /> : <PlayArrowIcon style={{ color: '#ff9800', fontSize: '20px' }} />}
                            </IconButton>

                            <IconButton size="small" onClick={() => setIsInlineMuted(!isInlineMuted)}>
                                {isInlineMuted ? <VolumeOffIcon style={{ color: '#888', fontSize: '18px' }} /> : <VolumeUpIcon style={{ color: '#fff', fontSize: '18px' }} />}
                            </IconButton>

                            <IconButton size="small" onClick={togglePublishAudio} title={isPublishingAudio ? "Stop Mic / Talkback" : "Start Mic / Talkback"}>
                                {isPublishingAudio ? <MicIcon style={{ color: '#ef5350', fontSize: '20px' }} /> : <MicOffIcon style={{ color: '#888', fontSize: '20px' }} />}
                            </IconButton>

                            <div className="vlc_small_info_text">
                                <span>{streamPrefix}-{formattedDevId}</span>
                            </div>


                            <IconButton size="small" onClick={() => setVlcModalOpen(true)} title="Maximize / Popout VLC Player">
                                <FullscreenIcon style={{ color: '#ff9800', fontSize: '22px' }} />
                            </IconButton>
                        </div>
                    </div>
                )}

                <LiveStreamModal
                    isOpen={vlcModalOpen}
                    onClose={() => setVlcModalOpen(false)}
                    deviceId={targetDeviceId}
                    deviceName={selectedCamObj?.name || selectedCamId}
                    streamPrefix={streamPrefix}
                />
            </div>
        </IonicPageWrapper>
    );
};

export default Camera;

const CameraCard = ({ camId, camName, subData, isMotion, isSelected, onSelect }) => {
    const timestampStr = subData.timestamp
        ? new Date(subData.timestamp * (subData.timestamp < 10000000000 ? 1000 : 1)).toLocaleTimeString()
        : 'N/A';

    return (
        <div className={`camera_card ${isMotion ? 'motion_detected_card' : 'normal_card'} ${isSelected ? 'selected_card' : ''}`}>
            <div className='title' style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                {camName}
            </div>

            {/* Notification Badge / Banner */}
            {isMotion ? (
                <div className="motion_notification_badge">
                    <WarningAmberIcon className="pulse_icon" style={{ fontSize: '18px', marginRight: '4px' }} />
                    <span>MOTION DETECTED</span>
                </div>
            ) : (
                <div className="normal_notification_badge">
                    <CheckCircleIcon style={{ fontSize: '16px', marginRight: '4px', color: '#2e7d32' }} />
                    <span>Normal</span>
                </div>
            )}

            <section className='camera_card_content' onClick={onSelect} style={{ cursor: 'pointer', textAlign: 'center', position: 'relative' }}>
                <VideocamIcon style={{ fontSize: '42px', color: isMotion ? '#d32f2f' : '#1976d2', marginTop: '10px' }} />
                <div style={{ fontSize: '11px', marginTop: '8px', color: '#555' }}>
                    Event: {subData.event || (isMotion ? 'motion_detected' : 'motion_cleared')}
                </div>
                <div style={{ fontSize: '10px', color: '#777' }}>
                    Time: {timestampStr}
                </div>
            </section>
        </div>
    );
};
